import React from 'react'
import EditIcon from 'assets/icons/edit-icon'
import RemoveIcon from 'assets/icons/remove-icon16px'
import EyeIcon from 'assets/icons/eye-2-line-icon'
import NominateIcon from 'assets/icons/nominate-icon'
import ViewIcon from 'assets/icons/view-icon'
import ReassessIcon from 'assets/icons/reassess-icon'
import {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_DOES_NOT_QUALIFY,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_NOT_ASSESSED_ID,
  SITE_STATUS_REVIEWED_BY_RFP_ID,
} from '../siteStatuses'
import { getEditOnlyContext, getEditOnlyStatusId } from '../../utils/editOnlyContext'
import { canViewSiteInMySites } from '../../utils/rolePermissions'
import {
  createNavigateIndividualConfirmedView,
  createNavigateIndividualDefaultViewSite,
  createNavigateIndividualReviewedByRfp,
  createNavigateReviewDetails,
  handleCreateMonitoring,
  handleGetMonitoring,
} from './navigationHandlers'
import AddMonitoringIcon from 'assets/icons/add-monitoring-icon'
import { buildMonitoringEvaluationUrl } from 'routing/constants'

function buildDraftOrEditOnlyItems(site, history, setSiteForDeleting, setEditOnlySiteToReject) {
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  return [
    {
      icon: <EditIcon />,
      title: 'edit',
      onClick: createNavigateReviewDetails(site, history, editOnlySite, editOnlySiteStatus),
    },
    {
      icon: <RemoveIcon />,
      title: 'Remove',
      onClick: () => {
        if (editOnlySiteStatus) {
          setEditOnlySiteToReject({ ...site, isApproved: false, delete: true })
          return
        }
        setSiteForDeleting(site)
      },
    },
  ]
}

function buildReviewedByRfpItems(site, history, setSiteToWorkWith) {
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  return [
    {
      icon: <EyeIcon />,
      title: 'review',
      onClick: createNavigateIndividualReviewedByRfp(site, history, editOnlySite, editOnlySiteStatus),
    },
    {
      icon: <NominateIcon />,
      title: 'nominateSite',
      onClick: () => {
        setSiteToWorkWith(site)
      },
    },
  ]
}

function buildConfirmedDelistedDnqItems(
  site,
  history,
  toggleModal,
  setSite,
  setSiteIsModalEditOpen,
  profile,
  isMySite,
  setMonitoringForDeleting
) {
  const { editOnlySite } = getEditOnlyContext(site)
  return [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      onClick: createNavigateIndividualConfirmedView(site, history, editOnlySite)
    },
    {
      icon: <ReassessIcon />,
      title: 'reassessSite',
      disabled: false,
      onClick: () => {
        toggleModal()
        setSite({
          id: site.id
        })
      }
    },
    {
      icon: <EditIcon />,
      title: 'editNow',
      disabled: false,
      onClick: () => {
        setSiteIsModalEditOpen(site)
      }
    },
    ...(() => {
      const siteEvaluations = Array.isArray(site?.monitoring)
        ? [...site.monitoring].sort((a, b) => Number(b.id) - Number(a.id))
        : []
      const confirmedMonitoring = siteEvaluations.find((entry) => entry?.monitoring_status === SITE_STATUS_CONFIRMED_ID)
      return confirmedMonitoring
        ? [
            {
              icon: <EyeIcon />,
              title: 'viewMonitoringEvaluation',
              disabled: false,
              onClick: () => {
                handleGetMonitoring(site.id, confirmedMonitoring.id, history)
              }
            }
          ]
        : []
    })(),
    (() => {
      const siteEvaluations = site?.monitoring
      const isUserCoProposer = site?.isUserCoProposer
      let targetEvaluation = null
      let lockedByOtherUser = false
      let alreadyEvaluated
      if (Array.isArray(siteEvaluations)) {
        targetEvaluation = siteEvaluations.find((entry) => {
          return entry && (entry.monitor_users_id === profile.id || isUserCoProposer) && entry.monitoring_status === SITE_STATUS_DRAFT_ID
        })
        lockedByOtherUser = siteEvaluations.some((entry) => {
          const isDraft = entry && entry.monitoring_status === SITE_STATUS_DRAFT_ID
          const isOwnedByOther = entry && entry.monitor_users_id !== profile.id
          return isDraft && isOwnedByOther
        })
        alreadyEvaluated = siteEvaluations.some((entry) => entry?.monitoring_status === SITE_STATUS_CONFIRMED_ID)
      }
      const title =
        targetEvaluation && alreadyEvaluated
          ? 'editMonitoringRevaluation'
          : targetEvaluation
            ? 'editMonitoringEvaluation'
            : alreadyEvaluated && !lockedByOtherUser
              ? 'reEvaluateMonitoring'
              : 'addMonitoringEvaluation'
      const isDisabled = lockedByOtherUser && !isUserCoProposer
      return {
        icon: targetEvaluation ? <EditIcon /> : <AddMonitoringIcon />,
        title,
        disabled: isDisabled,
        onClick: () => {
          if (isDisabled) return
          if (title === 'addMonitoringEvaluation' || title === 'reEvaluateMonitoring') {
            localStorage.setItem('isEdit', 'true')
          } else if (title === 'editMonitoringEvaluation' || title === 'editMonitoringRevaluation') {
            localStorage.setItem('isEdit', 'false')
          }
          if (targetEvaluation && (targetEvaluation.id || targetEvaluation.monitoringId)) {
            const monitoringId = targetEvaluation.id || targetEvaluation.monitoringId
            return history(buildMonitoringEvaluationUrl(site.id, monitoringId))
          }
          targetEvaluation
            ? handleGetMonitoring(site.id, targetEvaluation.id, history)
            : handleCreateMonitoring(site.id, profile.id, history)
        }
      }
    })(),
    ...(() => {
      const siteEvaluations = site?.monitoring
      let targetEvaluation = null
      if (Array.isArray(siteEvaluations)) {
        targetEvaluation = siteEvaluations.find((entry) => {
          return entry && entry.monitor_users_id === profile.id && entry.monitoring_status === SITE_STATUS_DRAFT_ID
        })
      }
      return isMySite && targetEvaluation
        ? [
            {
              icon: <RemoveIcon />,
              title: 'removeMonitoring',
              disabled: false,
              onClick: () => {
                setMonitoringForDeleting(targetEvaluation)
              }
            }
          ]
        : []
    })()
  ]
}

function buildNotAssessedItems(
  site,
  history,
  toggleModal,
  setSite,
  setSiteIsModalEditOpen,
  profile,
  isMySite,
  setMonitoringForDeleting
) {
  const { editOnlySite } = getEditOnlyContext(site)
  return [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      onClick: createNavigateIndividualConfirmedView(site, history, editOnlySite)
    },
    {
      icon: <ReassessIcon />,
      title: 'reassessSite',
      disabled: false,
      onClick: () => {
        toggleModal()
        setSite({
          id: site.id,
        })
      },
    },
    {
      icon: <EditIcon />,
      title: 'editNow',
      disabled: false,
      onClick: () => {
        setSiteIsModalEditOpen(site)
      }
    },
    ...(() => {
      const siteEvaluations = Array.isArray(site?.monitoring)
        ? [...site.monitoring].sort((a, b) => Number(b.id) - Number(a.id))
        : []
      const confirmedMonitoring = siteEvaluations.find((entry) => entry?.monitoring_status === SITE_STATUS_CONFIRMED_ID)
      return confirmedMonitoring
        ? [
            {
              icon: <EyeIcon />,
              title: 'viewMonitoringEvaluation',
              disabled: false,
              onClick: () => {
                handleGetMonitoring(site.id, confirmedMonitoring.id, history)
              }
            }
          ]
        : []
    })(),
    (() => {
      const siteEvaluations = site?.monitoring
      const isUserCoProposer = site?.isUserCoProposer
      let targetEvaluation = null
      let lockedByOtherUser = false
      let alreadyEvaluated
      if (Array.isArray(siteEvaluations)) {
        targetEvaluation = siteEvaluations.find((entry) => {
          return entry && (entry.monitor_users_id === profile.id || isUserCoProposer) && entry.monitoring_status === SITE_STATUS_DRAFT_ID
        })
        lockedByOtherUser = siteEvaluations.some((entry) => {
          const isDraft = entry && entry.monitoring_status === SITE_STATUS_DRAFT_ID
          const isOwnedByOther = entry && entry.monitor_users_id !== profile.id
          return isDraft && isOwnedByOther
        })
        alreadyEvaluated = siteEvaluations.some((entry) => entry?.monitoring_status === SITE_STATUS_CONFIRMED_ID)
      }
      const title =
        targetEvaluation && alreadyEvaluated
          ? 'editMonitoringRevaluation'
          : targetEvaluation
            ? 'editMonitoringEvaluation'
            : alreadyEvaluated && !lockedByOtherUser
              ? 'reEvaluateMonitoring'
              : 'addMonitoringEvaluation'
      const isDisabled = lockedByOtherUser && !isUserCoProposer
      return {
        icon: targetEvaluation ? <EditIcon /> : <AddMonitoringIcon />,
        title,
        disabled: isDisabled,
        onClick: () => {
          if (isDisabled) return
          if (title === 'addMonitoringEvaluation' || title === 'reEvaluateMonitoring') {
            localStorage.setItem('isEdit', 'true')
          } else if (title === 'editMonitoringEvaluation' || title === 'editMonitoringRevaluation') {
            localStorage.setItem('isEdit', 'false')
          }
          if (targetEvaluation && (targetEvaluation.id || targetEvaluation.monitoringId)) {
            const monitoringId = targetEvaluation.id || targetEvaluation.monitoringId
            return history(buildMonitoringEvaluationUrl(site.id, monitoringId))
          }
          targetEvaluation
            ? handleGetMonitoring(site.id, targetEvaluation.id, history)
            : handleCreateMonitoring(site.id, profile.id, history)
        }
      }
    })(),
    ...(() => {
      const siteEvaluations = site?.monitoring
      let targetEvaluation = null
      if (Array.isArray(siteEvaluations)) {
        targetEvaluation = siteEvaluations.find((entry) => {
          return entry && entry.monitor_users_id === profile.id && entry.monitoring_status === SITE_STATUS_DRAFT_ID
        })
      }
      return isMySite && targetEvaluation
        ? [
            {
              icon: <RemoveIcon />,
              title: 'removeMonitoring',
              disabled: false,
              onClick: () => {
                setMonitoringForDeleting(targetEvaluation)
              }
            }
          ]
        : []
    })()
  ]
}

function buildIndividualDefaultItems(site, history) {
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  return [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      onClick: createNavigateIndividualDefaultViewSite(site, history, editOnlySite, editOnlySiteStatus),
    },
  ]
}

export function buildIndividualMenuItems(ctx) {
  const {
    site,
    profile,
    isMySite,
    history,
    setSiteToWorkWith,
    setSiteForDeleting,
    toggleModal,
    setSite,
    setSiteIsModalEditOpen,
    setEditOnlySiteToReject,
    setMonitoringForDeleting
  } = ctx

  const statusId = getEditOnlyStatusId(site)
  if ([SITE_STATUS_DRAFT_ID, SITE_STATUS_EDIT_ONLY_ID].includes(statusId)) {
    if (!canViewSiteInMySites(site, profile)) {
      if (isMySite) {
        return []
      }
      return buildIndividualDefaultItems(site, history)
    }
    return buildDraftOrEditOnlyItems(site, history, setSiteForDeleting, setEditOnlySiteToReject)
  }
  if (statusId === SITE_STATUS_REVIEWED_BY_RFP_ID) {
    return buildReviewedByRfpItems(site, history, setSiteToWorkWith)
  }
  if (
    statusId === SITE_STATUS_CONFIRMED_ID ||
    statusId === SITE_STATUS_DELISTED_ID ||
    statusId === SITE_STATUS_DOES_NOT_QUALIFY
  ) {
    return buildConfirmedDelistedDnqItems(site, history, toggleModal, setSite, setSiteIsModalEditOpen, profile, isMySite, setMonitoringForDeleting)
  }
  if (statusId === SITE_STATUS_NOT_ASSESSED_ID) {
    return buildNotAssessedItems(site, history, toggleModal, setSite, setSiteIsModalEditOpen, profile, isMySite, setMonitoringForDeleting)
  }
  return buildIndividualDefaultItems(site, history)
}

export function parseIndividualMenuArgs(args) {
  const [
    site,
    profile,
    isMySite = false,
    history,
    setSiteToWorkWith,
    setSiteForDeleting,
    toggleModal,
    setSite,
    setSiteIsModalEditOpen,
    setEditOnlySiteToReject,
    setMonitoringForDeleting
  ] = args

  return {
    site,
    profile,
    isMySite,
    history,
    setSiteToWorkWith,
    setSiteForDeleting,
    toggleModal,
    setSite,
    setSiteIsModalEditOpen,
    setEditOnlySiteToReject,
    setMonitoringForDeleting
  }
}

export function getIndividualActionMenuItems(...args) {
  return buildIndividualMenuItems(parseIndividualMenuArgs(args))
}
